import {Employee} from './EmployeeApi';
import {ApiClient} from "./apiClient";
import {ApiResponse} from "./types";
import {LeaveType} from "./LeaveTypeApi";
import {appConfig} from "../config";
import {TimeRecordEntryType} from "./TimetrackingApi";

// ============================================================================
// Create API Client Instance
// ============================================================================

const requestApiClient = new ApiClient(`${appConfig.backendUrl}/api/requests`);

// ============================================================================
// Request Types
// ============================================================================

export interface AbstractRequest {
    id: string;
    employee: Employee;
    type: 'LEAVE' | 'COMMAND' | 'TIME_RECORD';
    description: string;
    state: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
    editable: boolean;
    cancellable: boolean;
    approvalSteps: any[];
    createdAt: string;
    updatedAt: string;
}

export interface LeaveRequest extends AbstractRequest {
    type: 'LEAVE';
    leaveStart: string;
    leaveEnd: string;
    leaveDays: number;
    leaveType: LeaveType;
    startHalfDay?: boolean;
    endHalfDay?: boolean;
}

export interface CommandRequest extends AbstractRequest {
    type: 'COMMAND';
    commandType: string;
    commandParameters: any;
}

// Specific command parameter interfaces
interface IncreaseBaseCompensationParameters {
    employeeId: string;
    hourlyRate: number;
    compensationType: 'HOURLY' | 'SALARY';
    validFrom: string;
}

// Specific command implementations
export interface IncreaseBaseCompensationCommand extends CommandRequest {
    commandType: 'INCREASE_BASE_COMPENSATION_AMOUNT';
    commandParameters: IncreaseBaseCompensationParameters;
}

// Union type of all specific command types
export type SpecificCommandRequest = IncreaseBaseCompensationCommand;

// ── Time Record Request ───────────────────────────────────────────────────────

/**
 * Snapshot of a single time entry as submitted by the employee.
 * Mirrors TimeRecordEntrySnapshot on the backend.
 */
export interface TimeRecordEntrySnapshot {
    entryType: TimeRecordEntryType;
    startTimestamp: string;
    endTimestamp: string | null;
    durationMinutes?: number | null;
}

export interface TimeRecordRequest extends AbstractRequest {
    type: 'TIME_RECORD';
    recordDate: string;
    baseRevision: number;
    previousEntries?: TimeRecordEntrySnapshot[];
    entries: TimeRecordEntrySnapshot[];
    actualMinutes?: number | null;
    breakMinutes?: number | null;
    actualSeconds?: number;
    notes?: string;
}

// ============================================================================
// Request DTOs
// ============================================================================

export interface LeaveRequestCreateRequest {
    employeeId: string;
    startDate: Date;
    endDate: Date;
    leaveTypeId?: number;
    comment?: string;
}

export interface LeaveRequestValidationRequest {
    employeeId: string;
    startDate: Date;
    endDate: Date;
    leaveTypeId?: number;
}

export interface LeaveRequestValidationResult {
    vacationDays: number;
    publicHolidays: number;
    remainingVacationDays: number;
}

/** Maps to TimeRecordRequestCreationContext on the backend. */
export interface TimeRecordRequestCreateRequest {
    employeeId: string;
    /** ISO-8601 date (yyyy-MM-dd) of the day being corrected. */
    recordDate: string;
    /** The intended corrected time entries. Must contain at least one entry. */
    entries: TimeRecordEntrySnapshot[];
    notes?: string;
}

// ============================================================================
// API Methods
// ============================================================================

/**
 * Fetch all command requests
 */
export const fetchAllCommandRequests = (): Promise<ApiResponse<SpecificCommandRequest[]>> => {
    return requestApiClient.get('?type=command');
};

/**
 * Fetch all leave requests
 */
export const fetchAllLeaveRequests = (): Promise<ApiResponse<LeaveRequest[]>> => {
    return requestApiClient.get('/leaves');
};

/**
 * Fetch all pending leave requests
 */
export const fetchPendingLeaveRequests = (): Promise<ApiResponse<LeaveRequest[]>> => {
    return requestApiClient.get('/leaves?state=pending');
};

/**
 * Create a new leave request
 */
export const createLeaveRequest = (
    requestData: LeaveRequestCreateRequest
): Promise<ApiResponse<LeaveRequest>> => {
    return requestApiClient.post('/leaves', requestData);
};

/**
 * Validate a leave request
 */
export const validateLeaveRequest = (
    requestData: LeaveRequestValidationRequest
): Promise<ApiResponse<LeaveRequestValidationResult>> => {
    return requestApiClient.post('/leaves/validate', requestData);
};

/**
 * Fetch all time record requests
 */
export const fetchAllTimeRecordRequests = (): Promise<ApiResponse<TimeRecordRequest[]>> => {
    return requestApiClient.get('/time-records');
};

/**
 * Fetch all pending time record requests
 */
export const fetchPendingTimeRecordRequests = (): Promise<ApiResponse<TimeRecordRequest[]>> => {
    return requestApiClient.get('/time-records?state=pending');
};

/**
 * Fetch all requests across all types, optionally filtered by state.
 * Calls GET /requests?state={state} — served by RequestController.
 */
export const fetchAllRequests = (state?: string): Promise<ApiResponse<AbstractRequest[]>> => {
    const query = state ? `?state=${state.toLowerCase()}` : '';
    return requestApiClient.get(`${query}`);
};

/**
 * Create a new time record correction request.
 */
export const createTimeRecordRequest = (
    requestData: TimeRecordRequestCreateRequest
): Promise<ApiResponse<TimeRecordRequest>> => {
    return requestApiClient.post('/time-records', requestData);
};

/**
 * Approve a request
 */
export const approveRequest = <T extends AbstractRequest>(
    request: T
): Promise<ApiResponse<T>> => {

    if (request.type === 'LEAVE') {
        return requestApiClient.patch(`/leaves/${request.id}/approve`, {});

    } else if (request.type === 'COMMAND') {
        return requestApiClient.patch(`/commands/${request.id}/approve`, {});

    } else if (request.type === 'TIME_RECORD') {
        return requestApiClient.patch(`/time-records/${request.id}/approve`, {});
    }

    return Promise.reject(new Error("Unknown request type"));
};

/**
 * Reject a request
 */
export const rejectRequest = <T extends AbstractRequest>(
    request: T
): Promise<ApiResponse<T>> => {

    if (request.type === 'LEAVE') {
        return requestApiClient.patch(`/leaves/${request.id}/reject`, {});

    } else if (request.type === 'COMMAND') {
        return requestApiClient.patch(`/commands/${request.id}/reject`, {});

    } else if (request.type === 'TIME_RECORD') {
        return requestApiClient.patch(`/time-records/${request.id}/reject`, {});
    }

    return Promise.reject(new Error("Unknown request type"));
};

/**
 * Cancel a request
 */
export const cancelRequest = <T extends AbstractRequest>(
    request: T
): Promise<ApiResponse<T>> => {

    if (request.type === 'LEAVE') {
        return requestApiClient.patch(`/leaves/${request.id}/cancel`, {});

    } else if (request.type === 'COMMAND') {
        return requestApiClient.patch(`/commands/${request.id}/cancel`, {});

    } else if (request.type === 'TIME_RECORD') {
        return requestApiClient.patch(`/time-records/${request.id}/cancel`, {});
    }

    return Promise.reject(new Error("Unknown request type"));
};